import { Static, Type } from '@sinclair/typebox';

// Données structurées extraites d'un site scrapé (cf. docs/website-preview-pulse-plan.md §2.3).
// Contrat unique entre les extracteurs (LLM générique, boosters API) et l'assemblage
// de la preview (Lot B4). Peu importe la source, le format de sortie est le même.

export const ExtractedBusinessSchema = Type.Object({
  name: Type.String(),
  bio: Type.Optional(Type.String()),
  phone: Type.Optional(Type.String()),
  email: Type.Optional(Type.String({ format: 'email' })),
  address: Type.Optional(Type.String()),
  // Coordonnées GPS de la fiche Google Places (Place Details `location`) — utilisées par
  // l'assembleur pour afficher la carte de la page Contact. Absentes si pas de match Places.
  latitude: Type.Optional(Type.Number()),
  longitude: Type.Optional(Type.Number()),
  // Horaires : { "lundi": "9h-18h", ... }
  business_hours: Type.Optional(Type.Record(Type.String(), Type.String())),
  // Logo affiché dans le header/footer de la preview — clé S3 (full-key, préfixée
  // ENVIRONMENT_VERSION) après ré-hébergement, URL externe entre l'extraction et le rehost.
  // Depuis le 02/11/2026 l'extracteur repère le logo du SITE PERSO (image nommée « logo » hors
  // favicon/sprite/icône, cf. `pickLogoCandidate`) ; le rehost le garde s'il fait au moins
  // `PREVIEW_QUALITY_THRESHOLDS.logo_min_side` px de côté. À défaut, il est généré (monogramme /
  // pictogramme métier, cf. PreviewLogoGeneratorService). Résout `{{pro:logo}}` au rendu.
  logo_url: Type.Optional(Type.String()),
  // Métiers de l'établissement (libellés FR courts au singulier, ex: « Coiffeur »,
  // « Barbier », « Esthéticienne »). PAS des prestations ni des catégories : le
  // métier exercé. Servent de clé de recherche fuzzy contre le référentiel
  // d'activités pour l'auto-rattachement à l'onboarding (Lot B5) — jamais affichés
  // tels quels.
  activity_labels: Type.Optional(Type.Array(Type.String())),
});
export type ExtractedBusinessObject = Static<typeof ExtractedBusinessSchema>;

/**
 * Une variante tarifaire d'une prestation (ex. « 1h » / « 1h30 », « cheveux
 * courts » / « cheveux longs »). Chaque variante devient une ligne
 * `prestation_price` distincte à la matérialisation du catalogue (Lot B2).
 * `label` est purement descriptif (jamais fondu dans le `name` de la prestation).
 */
export const ExtractedPrestationVariantSchema = Type.Object({
  label: Type.Optional(Type.String()),
  price_eur: Type.Optional(Type.Number()),
  duration_min: Type.Optional(Type.Number()),
});
export type ExtractedPrestationVariantObject = Static<typeof ExtractedPrestationVariantSchema>;

export const ExtractedPrestationSchema = Type.Object({
  name: Type.String(),
  description: Type.Optional(Type.String()),
  price_eur: Type.Optional(Type.Number()),
  duration_min: Type.Optional(Type.Number()),
  category: Type.Optional(Type.String()),
  photo_url: Type.Optional(Type.String()),
  // Variantes tarifaires (durées/prix multiples). `price_eur`/`duration_min` au
  // niveau prestation restent le tarif « par défaut » (1ère variante si absent).
  variants: Type.Optional(Type.Array(ExtractedPrestationVariantSchema)),
});
export type ExtractedPrestationObject = Static<typeof ExtractedPrestationSchema>;

export const ExtractedTeamMemberSchema = Type.Object({
  name: Type.String(),
  role: Type.Optional(Type.String()),
  bio: Type.Optional(Type.String()),
  photo_url: Type.Optional(Type.String()),
});
export type ExtractedTeamMemberObject = Static<typeof ExtractedTeamMemberSchema>;

export const ExtractedTestimonialSchema = Type.Object({
  author: Type.Optional(Type.String()),
  text: Type.String(),
  rating: Type.Optional(Type.Number()),
  source: Type.Optional(Type.String()),
});
export type ExtractedTestimonialObject = Static<typeof ExtractedTestimonialSchema>;

// Chiffre-clé affiché dans le bloc `key-figures` (ex. « +10 ans », « 1 200+ clients »).
// Contenu de « preuve sociale » : réel si dérivable (nb de prestations, note Google),
// sinon mock plausible sectoriel (cf. enrichExtracted). `value` est libre (texte court).
export const ExtractedKeyFigureSchema = Type.Object({
  value: Type.String(),
  label: Type.String(),
  icon: Type.Optional(Type.String()),
});
export type ExtractedKeyFigureObject = Static<typeof ExtractedKeyFigureSchema>;

// Question/réponse du bloc FAQ. Mock sectoriel par défaut (questions typiques du métier),
// jamais écrasé si une vraie FAQ a été scrapée.
export const ExtractedFaqSchema = Type.Object({
  question: Type.String(),
  answer: Type.String(),
});
export type ExtractedFaqObject = Static<typeof ExtractedFaqSchema>;

export const ExtractedBlogPostSchema = Type.Object({
  title: Type.String(),
  summary: Type.Optional(Type.String()),
  photo_url: Type.Optional(Type.String()),
  published_at: Type.Optional(Type.String()),
});
export type ExtractedBlogPostObject = Static<typeof ExtractedBlogPostSchema>;

export const WebsitePreviewExtractedSchema = Type.Object({
  business: ExtractedBusinessSchema,
  prestations: Type.Array(ExtractedPrestationSchema),
  team: Type.Optional(Type.Array(ExtractedTeamMemberSchema)),
  photos: Type.Optional(Type.Array(Type.String())),
  testimonials: Type.Optional(Type.Array(ExtractedTestimonialSchema)),
  // Chiffres-clés (« key-figures ») et FAQ : preuve sociale / réassurance affichée sur la home
  // et la page À propos. Réel si dérivable, sinon mock sectoriel (cf. ActivityDefaultContentService).
  key_figures: Type.Optional(Type.Array(ExtractedKeyFigureSchema)),
  faq: Type.Optional(Type.Array(ExtractedFaqSchema)),
  blog_posts: Type.Optional(Type.Array(ExtractedBlogPostSchema)),
  // Couleur de marque dominante détectée sur le site scrapé (hex `#rrggbb`), dérivée
  // de manière déterministe du HTML (meta theme-color, variables CSS, styles inline).
  // Appliquée au thème de la preview (cf. assembleur). Absente si le site n'expose
  // aucun signal couleur fiable (CSS externe non capturé) → fallback palette sectorielle.
  brand_color: Type.Optional(Type.String({ pattern: '^#[0-9a-fA-F]{6}$' })),
});
export type WebsitePreviewExtractedObject = Static<typeof WebsitePreviewExtractedSchema>;
